import * as React from 'react';

/**
 * Measures how much vertical room is left between an element's top edge and the
 * bottom of the viewport.
 *
 * Replaces the `height: calc(100vh - 195px)` pattern used by the data tables:
 * those magic offsets encode an assumption that the page toolbar above them is
 * exactly one row tall, which stops being true as soon as the toolbar wraps on
 * a narrow laptop. Measuring keeps the table bottom pinned to the viewport at
 * any toolbar height.
 *
 * @param bottomGutter space to leave below the element, in px.
 */
export const useAvailableHeight = <T extends HTMLElement = HTMLDivElement>(
  bottomGutter = 24,
): [React.RefObject<T>, string | undefined] => {
  const ref = React.useRef<T>(null);
  const [height, setHeight] = React.useState<string>();

  React.useEffect(() => {
    const measure = () => {
      const node = ref.current;
      if (!node) return;

      const { top } = node.getBoundingClientRect();
      const next = Math.max(window.innerHeight - top - bottomGutter, 240);

      setHeight(`${Math.round(next)}px`);
    };

    measure();

    window.addEventListener('resize', measure);

    // The toolbar above can change height without the window resizing (filters
    // wrapping, a selection banner appearing), so watch the layout too.
    const observer = new ResizeObserver(measure);
    if (document.body) observer.observe(document.body);
    if (ref.current?.parentElement) observer.observe(ref.current.parentElement);

    return () => {
      window.removeEventListener('resize', measure);
      observer.disconnect();
    };
  }, [bottomGutter]);

  return [ref, height];
};

export default useAvailableHeight;
